import { useState, useContext, useEffect, Dispatch, SetStateAction } from 'react';
import {
  MapContainer,
  AttributionControl,
} from 'react-leaflet';
import L from 'leaflet';
// Adds the gestureHandling map option, after Leaflet (it uses its global L)
import 'leaflet-gesture-handling';

import 'leaflet/dist/leaflet.css';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudSun } from '@fortawesome/pro-regular-svg-icons';

import {
  useAppDispatch,
  useAppSelector,
  hasPathsOnCurrentLayerSelector,
  setCustomRouteStart,
  setCustomRouteEnd,
  setShowCustomRoute,
  setCustomRouteFromWaypoint,
  preferencesSelector,
  mapSelector,
  isEmbeddedSelector,
} from '../../store';

import { ComponentLoadedContext } from '../../AppLoader';
import { LeafletContext } from '../../App';

import { Labels } from '../labels/Map/Labels';
import { Overlays } from '../overlays/Map/Overlays';
import { LayersControl } from './LayersControl';
import { Spaces } from '../spaces/Map/Spaces';
import { DisplayOptions } from './DisplayOptions';
import { RouteDisplay } from '../wayfinding/RouteDisplay';
import { WayfindingControls } from '../wayfinding/WayfindingControls';
import { CurrentLocationMarker } from '../wayfinding/CurrentLocation';
import { CustomRouteDisplay } from '../wayfinding/CustomRouteDisplay';
import { IndoorRouteSidebar } from '../wayfinding/IndoorRouteSidebar';
import { WaypointDestinationPicker } from '../wayfinding/WaypointDestinationPicker';
import { PathOverlay } from './PathOverlay';
import { DrawPath } from '../EditTools/DrawPath';

import { SidebarButton } from '../Sidebar';
import { Maximize } from './Maximize';
import { ZoomControl } from './ZoomControl';


import './Map.scss';

// see https://github.com/Leaflet/Leaflet/issues/4968
// FIX leaflet's default icon path problems with webpack
// @ts-ignore TS2339
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export const minZoom = 17;
export const maxZoom = 20;

interface MapProps {
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  setComponentLoaded: () => void;
  isWeatherSidebarOpen: boolean;
  setIsWeatherSidebarOpen: Dispatch<SetStateAction<boolean>>;
}

export const Map = ({
  setIsModalVisible,
  isSidebarOpen,
  setIsSidebarOpen,
  setComponentLoaded,
  isWeatherSidebarOpen,
  setIsWeatherSidebarOpen,
}: MapProps) => {
  const dispatch = useAppDispatch();
  const { componentLoaded } = useContext(ComponentLoadedContext);
  const { mapRef } = useContext(LeafletContext);
  const iMap = useAppSelector(mapSelector);
  const isEmbedded = useAppSelector(isEmbeddedSelector);

  const [showIndoorRouteSidebar, setShowIndoorRouteSidebar] = useState(false);
  const hasIndoorPaths = useAppSelector(hasPathsOnCurrentLayerSelector);
  const preferences = useAppSelector(preferencesSelector);

  const hasWeatherCoords = (preferences.weatherCoords ?? '').trim().length > 0;

  // Parse route params from URL on load (e.g., ?routeFrom=8989-atrium-ballroom&routeTo=8990-polaris)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const routeFrom = urlParams.get('routeFrom');
    const routeTo = urlParams.get('routeTo');
    const fromWaypoint = urlParams.get('from_waypoint');

    if (routeFrom && routeTo) {
      // Extract ID from id-slug format (e.g., "8989-atrium-ballroom" -> "8989")
      const extractId = (param: string) => param.split('-')[0];
      const startId = extractId(routeFrom);
      const endId = extractId(routeTo);
      if (startId && endId) {
        dispatch(setCustomRouteStart(startId));
        dispatch(setCustomRouteEnd(endId));
        dispatch(setShowCustomRoute(true));
      }
    } else if (fromWaypoint) {
      // QR code waypoint deep link - show destination picker
      dispatch(setCustomRouteFromWaypoint(fromWaypoint));
    }
  }, [dispatch]);

  // To prevent blank parts on the map when resizing the
  // visrez-interactive div without resizing the window.
  useEffect(() => {
    const visrezDiv = document.getElementById('visrez-interactive');
    const map = mapRef.current;
    if (!visrezDiv || !map) return;
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(visrezDiv);
    return () => resizeObserver.unobserve(visrezDiv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current]);

  return (
    <main className="map">
      <DisplayOptions />
      <SidebarButton
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <MapContainer
        ref={mapRef}
        center={[0, 0]}
        maxZoom={maxZoom}
        minZoom={minZoom}
        zoomSnap={0.25} // makes map.fitBounds() more accurate
        // Embedded in client pages, the page scrolls: the map zooms with Ctrl/⌘ + wheel,
        // and pans with two fingers on touch screens
        gestureHandling={isEmbedded}
        zoomControl={false}
        attributionControl={false}
        whenReady={() => setComponentLoaded()}
      >
        <Maximize />
        <AttributionControl
          prefix={false}
          position="bottomright"
        />
        {preferences.showZoomControls && (
          <ZoomControl position={preferences.zoomControlPosition || 'bottomright'} />
        )}
        <LayersControl />
        <Spaces
          openModal={() => setIsModalVisible(true)}
        />
        <Overlays />
        {iMap.allowLabels && (
          <Labels />
        )}

        <PathOverlay />

        <DrawPath />

        <CurrentLocationMarker />

        <RouteDisplay />

        {componentLoaded && <CustomRouteDisplay />}

      </MapContainer>

      <aside className="map__sidebar__buttons">
        {hasIndoorPaths && (
          <IndoorRouteSidebar
            isOpen={showIndoorRouteSidebar}
            onClose={() => setShowIndoorRouteSidebar(false)}
            onOpen={() => setShowIndoorRouteSidebar(true)}
          />
        )}
        <WayfindingControls />
        <WaypointDestinationPicker />

        {hasWeatherCoords && (
          <button
            type="button"
            className="weather-panel-launcher"
            onClick={() => setIsWeatherSidebarOpen((current) => !current)}
            aria-label={isWeatherSidebarOpen ? 'Close weather panel' : 'Open weather panel'}
          >
            <FontAwesomeIcon icon={faCloudSun} />
          </button>
        )}
      </aside>
    </main>
  );
};
